import { readContract } from '@wagmi/core';
import { nearestUsableTick, TickMath } from '@uniswap/v3-sdk';
import { Token } from '@uniswap/sdk-core';
import { useChainId } from 'wagmi';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { stateViewAbi } from '@generated/generated';
import { stateViewAddress } from '@generated/generated';
import { getPool } from '@/onchain/pools';
import { assetByAddress } from '@/onchain/assets';
import { tickToPriceScaled } from '@/utils/tickMath';
import { Bound } from '@/utils/types';

export type PoolDetailsAssetSymbolsParam = undefined | null | [] | [string] | [string, string];

type FetchedPoolDetails = Awaited<ReturnType<typeof fetchPoolDetails>>;

type FetchedPoolDetailsReturnType<T extends PoolDetailsAssetSymbolsParam> = T extends [
  string,
  string,
]
  ? FetchedPoolDetails
  : null;

/**
/**
 * Fetch the pool and position info for a given position token id
 * @param chainId - The chain id of the position
 *
 * @returns The pool details
 */
async function fetchPoolDetails(chainId: number, assetSymbols: [string, string]) {
  // 1. Get pool key info
  const pool = getPool(chainId, assetSymbols[0], assetSymbols[1]);
  const poolKey = pool.key;
  const poolKeyBytes32 = pool.keyBytes32;

  // 2. Get AssetConfigs
  const [token0Asset, token1Asset] = [
    assetByAddress(chainId, poolKey.currency0),
    assetByAddress(chainId, poolKey.currency1),
  ];

  if (token0Asset === undefined || token1Asset === undefined) {
    throw new Error(`Token assets for (${assetSymbols[0]}, ${assetSymbols[1]}) are invalid`);
  }
  // 3. Fetch pool state by pool key
  const onchainPoolState = (await readContract(wagmiConfig, {
    address: stateViewAddress[chainId as keyof typeof stateViewAddress],
    abi: stateViewAbi,
    functionName: 'getSlot0',
    args: [poolKeyBytes32],
  })) as readonly [bigint, number, number];

  // 4. Fetch pool liquidity
  const onchainPoolLiquidity = await readContract(wagmiConfig, {
    address: stateViewAddress[chainId as keyof typeof stateViewAddress],
    abi: stateViewAbi,
    functionName: 'getLiquidity',
    args: [poolKeyBytes32],
  });

  // Build the pool state from the fetched data
  const poolState = {
    sqrtPriceX96: onchainPoolState[0],
    tick: onchainPoolState[1],
    protocolFee: BigInt(onchainPoolState[2]),
    currentPriceScaled: tickToPriceScaled(Number(onchainPoolState[1])),
    liquidity: onchainPoolLiquidity,
  };

  // 4. Get the tickLower and tickUpper
  const tickSpacing = poolKey.tickSpacing;
  // Using 100 * tickSpacing to ensure the tick is within the range.
  // This can be improved when we have a better way to determine the tick range.
  const tickLower = nearestUsableTick(
    Math.max(poolState.tick - 100 * tickSpacing, TickMath.MIN_TICK),
    tickSpacing,
  );
  const tickUpper = nearestUsableTick(
    Math.min(poolState.tick + 100 * tickSpacing, TickMath.MAX_TICK),
    tickSpacing,
  );

  // reference: https://github.com/Uniswap/interface/blob/main/apps/web/src/hooks/useIsTickAtLimit.ts#L5
  // Check if the tick is at the limit
  const tickAtLimit = {
    [Bound.LOWER]: tickLower === nearestUsableTick(TickMath.MIN_TICK, poolKey.tickSpacing),
    [Bound.UPPER]: tickUpper === nearestUsableTick(TickMath.MAX_TICK, poolKey.tickSpacing),
  };
  // This will always be true because the tick is always in the range between the tickLower and tickUpper
  const tickInRange = true;

  // 5. Get the fee percentage relative to the fee basis points in decimal percentage representation
  const feePercent = poolKey.fee / 10_000 / 100;

  // 6. Generate the position tokens
  const positionTokens = [
    {
      asset: token0Asset,
      token: new Token(
        chainId,
        token0Asset.address[chainId],
        token0Asset.exponent,
        token0Asset.displaySymbol,
        token0Asset.name,
      ),
      symbol: token0Asset.displaySymbol,
      imageUrl: token0Asset.imageUrl,
    },
    {
      asset: token1Asset,
      token: new Token(
        chainId,
        token1Asset.address[chainId],
        token1Asset.exponent,
        token1Asset.displaySymbol,
        token1Asset.name,
      ),
      symbol: token1Asset.displaySymbol,
      imageUrl: token1Asset.imageUrl,
    },
  ];

  return {
    poolId: poolKeyBytes32,
    poolState,
    feePercent,
    tickLower,
    tickUpper,
    tickAtLimit,
    tickInRange,
    poolKey,
    positionTokens,
  };
}

export const QUERY_KEY_POOL_DETAILS = (
  chainId: number,
  assetSymbols: PoolDetailsAssetSymbolsParam,
) => ['usePoolDetails', chainId, ...[...(assetSymbols ?? [])].sort()];

const QUERY_FN_POOL_DETAILS = async function queryFnPoolDetails<
  T extends PoolDetailsAssetSymbolsParam,
>(chainId: number, assetSymbols: T): Promise<FetchedPoolDetailsReturnType<T>> {
  // This is a escape hatch for the case where the asset symbols are not valid
  if (assetSymbols == null || assetSymbols.length !== 2 || !assetSymbols[0] || !assetSymbols[1]) {
    return null as FetchedPoolDetailsReturnType<T>;
  }

  if (!chainId) {
    throw new Error('Chain ID is required');
  }

  const fetchedPoolDetails = await fetchPoolDetails(chainId, assetSymbols);

  return fetchedPoolDetails as FetchedPoolDetailsReturnType<T>;
};

/**
 * Fetch the pool details for a given set of asset symbols
 * @param assetSymbols - The array of asset symbols to fetch the pool details for
 * @returns The pool details
 */
export function usePoolDetails<T extends PoolDetailsAssetSymbolsParam>(
  assetSymbols: T,
): FetchedPoolDetailsReturnType<T> {
  const chainId = useChainId();

  const queryOptions = useMemo(
    () => ({
      queryKey: QUERY_KEY_POOL_DETAILS(chainId, assetSymbols),
      queryFn: async () => QUERY_FN_POOL_DETAILS(chainId, assetSymbols),
    }),
    [chainId, assetSymbols],
  );

  const { data: poolDetails } = useSuspenseQuery(queryOptions);

  return poolDetails;
}
